import { useState, useEffect } from 'react';
import { Search, Calendar, Home, IndianRupee, Mail, MessageCircle, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const INDIAN_STATES = [
  'All States', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

function SearchListings({ currentUser }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    state: 'All States',
    month: '',
    min_rooms: '',
    max_rent: ''
  });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state && filters.state !== 'All States') params.append('state', filters.state);
      if (filters.month) params.append('month', filters.month);
      if (filters.min_rooms) params.append('min_rooms', filters.min_rooms);
      if (filters.max_rent) params.append('max_rent', filters.max_rent);

      const response = await fetch(`${BACKEND_URL}/api/off-season/listings?${params.toString()}`);
      const data = await response.json();
      
      // Filter out own listings
      const filteredData = data.filter(listing => listing.resort_id !== currentUser.id);
      setListings(filteredData);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings();
  };

  const handleEmailClick = (listing) => {
    const subject = `Off-Season Collaboration - ${currentUser.name}`;
    const body = `Hi ${listing.resort_name},\n\nI'm ${currentUser.name}, and I noticed your off-season availability listing on Travelodges.\n\nI'm interested in discussing collaboration opportunities.\n\nBest regards,\n${currentUser.name}\n${currentUser.email}\n${currentUser.phone}`;
    
    window.location.href = `mailto:${listing.resort_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleWhatsAppClick = (listing) => {
    const message = `Hi ${listing.resort_name}, I'm ${currentUser.name} from Travelodges network. I'm interested in your off-season availability listing. Let's discuss collaboration opportunities.`;
    const whatsappUrl = `https://wa.me/${listing.resort_phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Search Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <Search className="h-5 w-5 inline mr-2" />
            Search Off-Season Listings
          </CardTitle>
          <p className="text-sm text-gray-600">
            Search other resorts' off-season availability and contact them directly.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* State Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                >
                  {INDIAN_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Month Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                <select
                  value={filters.month}
                  onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                >
                  <option value="">All Months</option>
                  {MONTHS.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>

              {/* Min Rooms Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min. Rooms</label>
                <Input
                  type="number"
                  value={filters.min_rooms}
                  onChange={(e) => setFilters({ ...filters, min_rooms: e.target.value })}
                  placeholder="e.g., 5"
                  min="1"
                />
              </div>

              {/* Max Rent Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max. Rent</label>
                <Input
                  type="number"
                  value={filters.max_rent}
                  onChange={(e) => setFilters({ ...filters, max_rent: e.target.value })}
                  placeholder="e.g., 5000"
                  min="0"
                />
              </div>
            </div>

            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
              <Search className="h-4 w-4 mr-2" />
              Search Listings
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <div>
        <div className="mb-4">
          <p className="text-gray-600">
            {loading ? 'Searching...' : `Found ${listings.length} listing${listings.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {listings.length === 0 && !loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Listings Found</h3>
              <p className="text-gray-600">Try adjusting your search filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <Card key={listing.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{listing.resort_name}</CardTitle>
                  <p className="text-sm text-gray-600 flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    {listing.location}, {listing.state}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Months */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="h-4 w-4 inline mr-2" />
                      Available Months
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {listing.off_season_months.slice(0, 4).map((month) => (
                        <Badge key={month} variant="outline" className="text-xs">
                          {month.substring(0, 3)}
                        </Badge>
                      ))}
                      {listing.off_season_months.length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{listing.off_season_months.length - 4}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">
                        <Home className="h-4 w-4 inline mr-1" />
                        Rooms
                      </p>
                      <p className="font-semibold">{listing.rooms_available}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        <IndianRupee className="h-4 w-4 inline mr-1" />
                        Min. Rent
                      </p>
                      <p className="font-semibold">₹{listing.minimum_rent.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Optional Notes */}
                  {listing.optional_notes && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700 line-clamp-2">{listing.optional_notes}</p>
                    </div>
                  )}

                  {/* Contact Buttons */}
                  <div className="flex space-x-2 pt-4 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                      onClick={() => handleEmailClick(listing)}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-green-600 text-green-600 hover:bg-green-50"
                      onClick={() => handleWhatsAppClick(listing)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchListings;
